/* eslint-disable no-mixed-spaces-and-tabs */
import { Component, Prop, State, h, Element, Event, EventEmitter, Listen } from '@stencil/core';
import { FormData, AnyFormElement, Action, Plugin, Consent } from './forms-ui-form-faas.interface';
import { renderFields } from './services/render-fields';
import { IConsentService, createConsentService } from './services/consent-service';
import { createMetadataRetriever } from './services/metadata-retriever';
import {
	getZoomInfoPlugin, hideAllElementsExceptZoomInfoSearch, showFields,
	injectZoomInfo, removeZoomInfo,
} from './services/zoominfo';
import { createFormSubmissionService, IFormSubmissionService, SubmissionStatus } from './services/submit-form';
import { CONSTANTS } from './constant';
import { createNucaptchaService, INucaptchaService } from './services/nucaptcha-service';
import { createFormValidator, IFormValidator } from './services/form-validator';
import { createFaaSDataClient, IFaaSDataClient } from './api/faas-data';
import { FormLoadEvent, FormSubmitErrorEvent, FormSubmitSuccessEvent, FormDropOffEvent, FormLoadErrorEvent } from './form-events.interface';

const PARDOT_KEY = 'PARDOT';

@Component({
	tag: 'forms-ui-form',
	styleUrl: 'forms-ui-form.scss',
	shadow: false,
})

export class FormsUiForm {
	/** form data URL */
	@Prop() formId: string;

	/** locale used for variant api call */
	@Prop() locale: string;

	/** base URL used for retrieving metadata for published forms */
	@Prop() apiBaseUrl: string = 'https://www.mastercard.com/marketingservices/public/form-services/data/';

	/** optional property used for retrieving metadata for draft and modifies forms */
	@Prop({ mutable: true }) metadataRetrievalUrl: string;

	/** whether or not to submit the form in test mode */
	@Prop() testMode: boolean = false;

	/** holds error state */
	@State() _error: boolean = false;

	/** sets error message */
	@State() _errorMessage: string = 'Failed to fetch form data.';

	/** holds error state */
	@State() _toastError: boolean = false;

	/** holds success state */
	@State() _success: boolean = false;

	/** allows for the form submission to be disabled while submitting... */
	@State() _isLoading: boolean = false;

	/** tracks zoomInfo consent state */
	@State() _zoomInfoRejected: boolean = false;

	/** tracks state of consents */
	@State() consents: Consent[];

	/** Input element */
	@Element() host: HTMLFormsUiFormElement;

	/** toast event */
	@Event({ eventName: 'toast' }) toastEvent: EventEmitter;

	/** toast listener */
	@Listen('toast', { target: 'document' })
	toastHandler(event: CustomEvent) {
		this.toastErrorMessage = event.detail.message;
		this.toastVariant = event.detail.variant;
		this.toastLight = event.detail.light;
		this.toastTimeout = event.detail.timeout;
		this._toastError = true;
	}

	@Listen('formsUIFieldUpdate')
	updateElement(event: CustomEvent) {
		if (this.host.contains(event.target as Node)) {
			this.modifiedFields.add(event.detail);
		}
	}

	/** tracks modified fields */
	private modifiedFields: Set<string> = new Set<string>();

	/** Holds array of formelements generated by render function */
	private formRefs: AnyFormElement[] = [];

	/** Forms UI Form element */
	private formRef: HTMLFormElement;

	/** holds zoomInfo plugin data */
	private _zoomInfoPlugin: Plugin;

	/** holds form metadata extracted from FaaS API */
	private formData: FormData;

	/** indicates whether the 'light' variant of the toast should be used */
	private toastLight: boolean = false;

	/** sets the toast timeout */
	private toastTimeout: number = null;

	/** tracks the toast variant used in the form */
	private toastVariant: 'success' | 'information' | 'error' = 'error';

	/** tracks the toast error message */
	private toastErrorMessage: string = 'Something went wrong. Please try again later';

	private faasDataClient: IFaaSDataClient;

	private nucaptchaService: INucaptchaService;

	private submissionService: IFormSubmissionService;

	private formValidator: IFormValidator;

	private consentService: IConsentService;

	/** Adds form reference to array */
	addFormRef(ref: AnyFormElement): void {
		this.formRefs.push(ref);
	}

	/** Runs only one time before a component loads */
	async componentWillLoad() {
		this.metadataRetrievalUrl = this.metadataRetrievalUrl || this.apiBaseUrl;
		this.faasDataClient = createFaaSDataClient(
			this.apiBaseUrl,
			this.metadataRetrievalUrl,
			this.formId,
			this.locale,
		);
		try {
			this.formData = await createMetadataRetriever(this.faasDataClient).getFormMetadata();
			this._error = false;
		} catch (error) {
			this._error = true;
			this._errorMessage = error.message;
			const errorEvent: FormLoadErrorEvent = {
				formID: this.formId,
				errorMsg: `FormLoadError|${error.message}`,
			};
			window.dispatchEvent(new CustomEvent('formLoadError', { detail: errorEvent }));
			return;
		}

		this._zoomInfoPlugin = getZoomInfoPlugin(this.formData);
		this.loadConsents();

		/*
		 * We have to create these service after form data is fetched since they depend
		 * on information included in the form data
		 */
		this.nucaptchaService = createNucaptchaService({
			label: this.formData.captcha.label,
			validationMessage: this.formData.captcha.validationText,
			reportSegment: this.formData.reportSegment || CONSTANTS.DEFAULT_REPORT_SEGMENT,
			clientId: CONSTANTS.NUCAPTCHA_CLIENT_ID,
		});
		this.consentService = createConsentService();
		this.formValidator = createFormValidator(this.consentService);
		this.submissionService = createFormSubmissionService(
			this.nucaptchaService,
			this.consentService,
			this.faasDataClient,
			this.formData,
			this.testMode,
		);

		/** trigger analytics event for completed fields */
		window.addEventListener('beforeunload', this.triggerDropOff.bind(this), true);
	}

	triggerDropOff() {
		/*
		 * some modified fields reported come from composed elements (eg. selects in date picker)
		 * we need to get rid of these by matching changed fields against original form data
		 */
		const fieldNames = this.formData.formFields.map((field) => field.name);
		const modifiedFieldsArray = Array.from(this.modifiedFields)
			.filter((modifiedFieldName) => fieldNames.includes(modifiedFieldName));
		const dropOffEvent: FormDropOffEvent = {
			formID: this.formId,
			zoomInfo: this._zoomInfoPlugin !== null && !this._zoomInfoRejected,
			formBusinessType: this.formData.businessType,
			formTimestamp: Date.now(),
			completedFields: {
				labels: modifiedFieldsArray,
				count: modifiedFieldsArray.length,
			},
		};
		window.dispatchEvent(new CustomEvent('formDropOff', { detail: dropOffEvent }));
	}

	/** Runs only one time after a component loads */
	componentDidLoad() {
		/** ZoomInfo processing should only begin after the form has been rendered */
		if (this._zoomInfoPlugin) {
			hideAllElementsExceptZoomInfoSearch(this.formRefs);
			injectZoomInfo(this.formRefs, this._zoomInfoPlugin.pluginMetaData.projectKey);
		}
		if (this.formId && this.formData) {
			const loadEvent: FormLoadEvent = {
				formID: this.formId,
				formType: this.formData.reportSegment || '',
				version: this.formData.version || 1,
				zoomInfo: this._zoomInfoPlugin !== null && !this._zoomInfoRejected,
				formBusinessType: this.formData.businessType,
			};
			window.dispatchEvent(new CustomEvent('formLoad', { detail: loadEvent }));
		}

		/**
		 * Adds event listener to trigger focus
		 * First component on page gets its focusComponent() method called.
		 */
		this.formRef?.addEventListener('focusForm', () => {
			if (this.formRefs.length > 0) this.formRefs[0].focusComponent();
		});
	}

	/** Resets formRefs on re-render to ensure refs match reality */
	componentWillRender() {
		this.formRefs = [];
	}

	/** Handle Form Submission */
	async handleFormSubmit() {
		this.formRefs = this.formRefs.filter((ref) => ref);
		const isFormInvalid = !(await this.formValidator.isFormValid(this.formRefs));
		if (isFormInvalid) {
			/* eslint-disable no-console */
			console.error('errors need to be fixed first!');
			const errorField = this.formRefs.find((el) => el.error);
			this.scrollToErrorField(errorField);
		} else {
			this._isLoading = true;
			let submitRequest: Promise<SubmissionStatus>;
			const pardotAction = this.formData.actions
				.find((eachObject: Action) => eachObject.formAction === PARDOT_KEY);
			if (pardotAction) {
				const pardotActionUrl = pardotAction.pardotDetails.url;
				submitRequest = this.submissionService.submitToPardot(
					pardotActionUrl,
					this.formRef,
					this.formRefs,
				);
			} else {
				submitRequest = this.submissionService.submitToDb(this.formRefs);
			}

			try {
				const submitStatus = await submitRequest;
				switch (submitStatus) {
					case SubmissionStatus.NucaptchaRequired:
						this._errorMessage = 'Nucaptcha required';
						break;
					case SubmissionStatus.Error:
						this._errorMessage = 'Failed to submit form';
						this._error = true;
						break;
					case SubmissionStatus.Success:
						window.removeEventListener('beforeunload', this.triggerDropOff.bind(this), true);
						this._errorMessage = '';
						this.handleSuccessfulSubmission();
						break;
					default:
				}
			} catch (error) {
				this._error = true;
			} finally {
				this._isLoading = false;
				if (this._error) {
					const submitErrorEvent: FormSubmitErrorEvent = {
						formID: this.formId,
						formType: this.formData?.reportSegment,
						version: this.formData.version,
						zoomInfo: this._zoomInfoPlugin !== null && !this._zoomInfoRejected,
						formBusinessType: this.formData.businessType,
						errorMsg: `FormSubmitError|${this._errorMessage}`,
					};
					window.dispatchEvent(new CustomEvent('formSubmitError', { detail: submitErrorEvent }));
				}
			}
		}
	}

	scrollToErrorField(element: AnyFormElement) {
		if (element) {
			element.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}

	/**
	 * Handles opt-out checkbox.
	 *	 - removes zoomInfo scripts from page
	 *	 - deletes input elements associated with zoomInfo matching
	 *	 - sets zoomInfo rejection state to trigger a re-render
	 */
	handleZoomInfoOptOut(e: any) {
		if (e.target.checked) {
			removeZoomInfo();
			const zIFieldsToDelete = [
				...Array.from(document.querySelectorAll('[data-element="businessEmailAddress"]')),
				...Array.from(document.querySelectorAll('[data-element="emailAddress"]'))];
			zIFieldsToDelete.forEach((elem) => elem.remove());

			showFields(this.formRefs);
			this._zoomInfoRejected = true;
			this.loadConsents();
		}
	}

	/**
	 * There can only be one ZoomInfo consent.
	 * Therefore, if ZoomInfo is enabled on a form,
	 * the author must provide consent text that encompasses all other necessary consent text.
	 * If the user opts out of ZoomInfo, the form will display all other non-zoominfo consents
	 */
	loadConsents() {
		if (this._zoomInfoPlugin && !this._zoomInfoRejected) {
			this.consents = this.formData.consents.filter((consent) => consent.zoomInfoConsent);
		} else {
			this.consents = this.formData.consents.filter((consent) => !consent.zoomInfoConsent);
			this.consents.forEach((consent) => consent.accepted = false);
		}
	}

	handleSuccessfulSubmission() {
		const submitSuccessEvent: FormSubmitSuccessEvent = {
			formID: this.formId,
			formType: this.formData?.reportSegment,
			version: this.formData.version,
			zoomInfo: this._zoomInfoPlugin !== null && !this._zoomInfoRejected,
			formBusinessType: this.formData.businessType,
			formTimestamp: Date.now(),
			successAction: {
				action: this.formData.redirectPageUrl ? 'redirect' : 'success message',
			},
		};

		window.dispatchEvent(new CustomEvent('formSubmitSuccess', { detail: submitSuccessEvent }));

		if (this.formData.downloadFileUrl) {
			this.downloadFile(this.formData.downloadFileUrl);
		}
		if (this.formData.successMessage) {
			this._success = true;
		}
		if (this.formData.redirectPageUrl) {
			window.location.href = this.formData.redirectPageUrl;
		}
		this.nucaptchaService.cleanUpCaptcha();
	}

	renderSuccessMessage() {
		return <div><h2 class="success-message-h2">Thank you!</h2>
			<h4 class="success-message-h4">We have received your submission.</h4>
			<p class="success-message-p">{this.formData.successMessage}</p></div>;
	}

	renderErrorMessage() {
		return <div>
			<h2 class="error-message-h2">Oops! Something Went Wrong!</h2>
			<h4 class="error-message-h4">
				We are sorry but it looks like there was an issue with your submission.
				Please try again or contact our support team if the problem persists.</h4>
			<p class="error-message-p">Possible Solutions:</p>
			<ul class="error-message-list">
				<li>Check your internet connection.</li>
				<li>Ensure all required fields are filled out correctly.</li>
				<li>Try refreshing the page.</li>
			</ul>
			<button class="error-message-button" type="button" onClick={() => this._error = false}>
				Try Again
			</button>
		</div>;
	}

	renderToast() {
		if (this._toastError) {
			this._toastError = false;
			return <forms-ui-toast
				message={this.toastErrorMessage}
				variant={this.toastVariant}
				light={this.toastLight}
				timeout={this.toastTimeout}>
			</forms-ui-toast>;
		}
		return null;
	}

	/** download file from URL */
	downloadFile(url: string) {
		const fileName = url.substring(url.lastIndexOf('/') + 1);
		const link = document.createElement('a');
		link.href = url;
		link.style.visibility = 'hidden';
		link.setAttribute('target', '_blank');
		link.setAttribute('download', fileName);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

	render() {
		if (this._success) {
			return this.renderSuccessMessage();
		}
		if (this._error) {
			return this.renderErrorMessage();
		}
		return this.formData
			&& <form id={ this.formData.id } ref={(elem) => this.formRef = elem}>
				<h2>{ this.formData.title }</h2>
				<h4>{ this.formData.description || '\u00A0' }</h4>
				<div class="forms-ui-form-content-container">
					<div class="forms-ui-fields">
						{renderFields(this.formData, this.addFormRef.bind(this), this._zoomInfoRejected)}
					</div>
					{
						/** render consents */
						this.consentService.renderAndManageConsents(this.consents)
					}
					{
						/** zoominfo opt-out checkbox */
						(this._zoomInfoPlugin && !this._zoomInfoRejected)
							&& <div class="forms-ui-zi-optout"><forms-ui-checkbox
								required={false}
								label={this._zoomInfoPlugin.pluginMetaData.label}
								onFormsUICheckboxUpdate={/* istanbul ignore next */ (e) => this.handleZoomInfoOptOut(e)}
							/></div>
					}
				</div>
				{
					this.nucaptchaService.renderCaptchaContainer()
				}
				<forms-ui-button
					label='Submit'
					onClick={/* istanbul ignore next */ () => this.handleFormSubmit()}
					disabled={this._isLoading}>
				</forms-ui-button>
				{
					this.renderToast()
				}
			</form>;
	}
}
